import * as ng from 'angular';
import * as _ from 'underscore';
import * as xlsx from 'xlsx';
import * as CSV from 'csv-js/src/csv.js';

import {UtilsService, ApiHelperService} from '../../shared';

// Since intlTelInput doesn't export a js module for use with raw text
// (they intend to only use their module with jquery input elements),
// it is necessary to 'declare' the utils library so we can access
// the methods they use for validation.
declare var intlTelInputUtils: any;

ng.module('e360')
   .factory('ImporterService', importerServiceFactory);

importerServiceFactory.$inject = ['$q', 'utils', 'session', 'apiHelper'];
function importerServiceFactory($q:ng.IQService, utils, session, apiHelper) {
   ImporterService.$q = $q;
   ImporterService.utils = utils;
   ImporterService.session = session;
   ImporterService.apiHelper = apiHelper;

   return ImporterService;
}

export class ImporterService {
   //dependancy injections
   static $q:ng.IQService;
   static utils:UtilsService;
   static session;
   static apiHelper:ApiHelperService;

   formatError: string;
   dataErrors = false;
   emails: string[];
   wristBands: string[];
   map: any;
   headers: any[];

   static fieldMap = {
      firstName: 'First Name',
      middleInitial: 'Middle Initial',
      lastName: 'Last Name',
      title: 'Title',
      address1: 'Address 1',
      address2: 'Address 2',
      city: 'City',
      state: 'State',
      postalCode: 'Zip/Postal Code',
      country: 'Country',
      phone: 'Phone',
      email: 'Email Address',
      company: 'Company',
      dateOfBirth: 'Date of Birth',
      gender: 'Gender',
      locale: 'Locale',
      unsubscribed: 'Unsubscribed',
      salesOptin: 'Opt-in (Sales)',
      promoOptin: 'Opt-in (Promo)',
      contestOptin: 'Opt-in (Contest)',
      contactOptin: 'Opt-in (Contact)',
      guestType: 'Guest Type'
   };

   static typeMap = {
      participants: {
         id: 'participants',
         api: 'participant',
         key: 'participants',
         title: 'Participants'
      },
      prereg: {
         id: 'prereg',
         api: 'prereg',
         key: 'preregs',
         title: 'Pre-registrants'
      },
      invitees: {
         id: 'invitees',
         api: 'invitee',
         key: 'invitees',
         title: 'Invitees'
      }
   };

   static $inject = [];

   constructor(
      private data: any,
      private type: any,
      private surveyData: any,
      private event: any,
      private timeslotId: number,
      private guestTypes: string[],
      private allowDupEmail: boolean,
      private clearInvalidPhone: boolean
   ) {
      this.map = this.getFields();
      this.headers = this.getHeaders();

      this.parseDataErrors();
   }

   static readFile(file: any, type: any, surveyData: any, event: any, timeslotId: number, guestTypes: string[], allowDupEmail: boolean, clearInvalidPhone: boolean): ng.IPromise<any> {
      let reader = new FileReader();
      let deferred = ImporterService.$q.defer();

      reader.onload = (e: any) => {
         let data: any;
         let ext = file.name.split('.').pop();
         try {
            if (['xls', 'xlsx', 'xlsm', 'xlsb'].indexOf(ext) > -1) {
               let workbook = xlsx.read(e.target.result, { type: 'binary' });
               data = xlsx.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
            } else if (ext == 'csv') {
               data = CSV.parse(e.target.result);
            }

            if (data != null) {
               let importerService = new ImporterService(data, type, surveyData, event, timeslotId, guestTypes, allowDupEmail, clearInvalidPhone);
               deferred.resolve(importerService);
            } else {
               deferred.reject(null);
            }
         } catch (err) {
            deferred.reject(err);
         }
      };

      reader.readAsBinaryString(file);

      return deferred.promise;
   }

   static getType(type: string): any {
      return ImporterService.typeMap[type];
   }

   import(triggerDataEvents:boolean): ng.IPromise<ng.IHttpPromiseCallbackArg<any>> {
      let toRemove = [];
      for (let header of this.headers) {
         if ((header.selected === '' && header.survey === false)
            || typeof header.survey == 'undefined') {
            toRemove.push(header.current);
         }
      }

      // prune skipped columns
      for (let data of this.data) {
         for (let header of toRemove) {
            if (data[header]) {
               delete data[header];
            }
         }
      }

      let url = 'import/' + this.type.api;
      let obj: any = {};

      obj.campaignId = ImporterService.session.getCampaign().id;
      obj.eventId = this.event.id;
      obj.triggerDataEvents = triggerDataEvents;

      this.data = this.prepareData();

      if (this.timeslotId) {
         obj.timeSlotId = this.timeslotId;
         for (let e of this.data) {
            e.timeSlotId = this.timeslotId;
         }
      }

      obj[this.type.key] = this.data;

      return ImporterService.apiHelper.post(url, obj);
   }

   renameProperty(oldProp: string, newProp: string, skipParse: boolean) {
      if (oldProp == newProp) {
         return;
      }

      if (!newProp) {
         this.parseDataErrors();
         return;
      }

      if (this.headers != null) {
         for (let header of this.headers) {
            if (header.current == oldProp) {
               header.current = newProp;
            }
         }
      }

      for (let data of this.data) {
         data[newProp] = data[oldProp];
         delete data[oldProp];
      }

      if (!skipParse) this.parseDataErrors();
   }

   private parseDataErrors() {
      this.dataErrors = false;

      if (this.headers != null) {
         for (let header of this.headers) {
            header.errors = [];
         }
      }

      this.emails = [];
      this.wristBands = [];

      let line = 1;
      for (let data of this.data) {
         data.surveyItems = [];

         for (let prop in data) {
            let value = data[prop];
            let errors = [];

            let header = _.find(this.headers, ((h: any) => h.selected == prop || (h.current == prop && h.survey)));

            if (header && header.survey) {
               errors = this.validateSurvey(header.survey, value);
            } else {
               errors = this.validateProperty(prop, value, data);
            }

            if (header && errors.length > 0) {
               this.dataErrors = true;

               header.errors.push({
                  line: line,
                  value: value,
                  errors: errors
               });
            }
         }
         line++;
      }
   }

   private prepareData() {
      for (let ek in this.data) {
         let e = this.data[ek];
         for (let k in e) {
            let v = e[k];

            if (v === '') { //replace empty strings in data with nulls to avoid unnecessary upload errors
               this.data[ek][k] = null;
            }

            // Cast booleans
            if (['contactOptin', 'salesOptin', 'promoOptin', 'contestOptin', 'unsubscribed', 'isConfirmed'].indexOf(k) > -1) {
               this.data[ek][k] = ImporterService.utils.parseBoolean(v);
            }

            // Add dial code to phone number
            if (k === 'phone') {
               let countryCode = e.country ? e.country : this.event.state.countryCode;
               this.data[ek][k] = intlTelInputUtils.formatNumber(this.data[ek][k], countryCode);
            }

            if (k.substr(0, 11).toLowerCase() == 'surveyitem.' && v && v.trim().length > 0) {
               let survey = this.surveyData[k.substr(11).toLowerCase()];
               let value = v;
               // Ensure 1 of each option is actually submitted
               if (survey.type == 'Multioption') {
                  let options = v.split(',');
                  let vals = [];
                  for (let opt of options) {
                     if (vals.indexOf(opt.trim()) === -1) {
                        vals.push(opt.trim());
                     }
                  }
                  value = vals.join(',');
               }

               if (!this.data[ek].surveyItems) {
                  this.data[ek].surveyItems = [];
               }

               this.data[ek].surveyItems.push({
                  name: survey.key,
                  value: survey.type == 'Boolean' ? ImporterService.utils.parseBoolean(value) : value
               });

               delete this.data[ek][k];
            }
         }
      }
      return this.data;
   }

   private getFields(): any {
      let map: any = ng.copy(ImporterService.fieldMap);

      if (this.type.id == 'participants') {
         map.notes = 'Notes';
         map.ageCategory = 'Age Category';
         map.wristBandId = 'WristbandID';
         map.phone = 'Phone (Mobile)';
         map.phoneHome = 'Phone (Home)';
         map.phoneWork = 'Phone (Work)';
         map.createdDate = 'Date Created';
      } else if (this.type.id == 'prereg') {
         map.notes = 'Notes';
         map.isConfirmed = 'Confirmed';
         map.source = 'Source';
         map.ageCategory = 'Age Category';
      }

      return map;
   }

   private getHeaders(): any[] {
      let headers = [];
      let keys = [];

      for (let entity of this.data) {
         for (let k in entity) {
            let e = entity[k];

            if (!k || k == 'undefined') { // xlsx parser will set unknown headers as a string "undefined"
               this.formatError = 'Your data columns did not match the number of header columns.';
               return null;
            }

            //find header from $scope.map if possible based on k (e.g. if k = 'First Name' then header = 'firstName')
            let header = _.findKey(this.map, (v) => {
               return v === k;
            });

            //fallback if header wasn't found in map (csv title is 'firstName' rather than 'First Name') or k is a survey item
            header = header ? header : k.charAt(0).toLowerCase() + k.slice(1);

            if (keys.indexOf(header.toLowerCase()) > -1) {
               break;
            }

            keys.push(header.toLowerCase());

            let isSurvey = k.substr(0, 11).toLowerCase() == 'surveyitem.';
            this.renameProperty(k, header, true);

            headers.push({
               current: header,
               selected: this.map[header] ? header : '',
               original: k,
               survey: isSurvey ? this.surveyData[k.substr(11).toLowerCase()] : false
            });
         }
      }

      headers = _.sortBy(headers, 'original');

      return headers;
   }

   private validateSurvey(data: any, value: string) {
      if (!value || value.trim().length === 0) {
         return [];
      }

      if (!data) {
         return ['A survey with this key does not exist'];
      }

      if (data.type == 'Boolean' && !ImporterService.utils.isBoolean(value)) {
         return ['This survey response must be set to true or false'];
      }

      if (data.type == 'Multioption' || data.type == 'Option') {
         let options = value.split(',');
         let unmatched = [];

         for (let option of options) {
            let exists = _.find(data.surveyItemChoices, (choice: any) => {
               return choice.key.toLowerCase() == option.toLowerCase();
            });

            if (!exists) {
               unmatched.push(option);
            }
         }

         if (unmatched.length > 0) {
            return ['The following survey choice option(s) are invalid: ' + unmatched.join(', ')];
         }
      }

      return [];
   }

   /**
    * Returns a validation message for the given property type and value
    * @param prop The name of the property to be validated
    * @param val The value of the property to be validated
    * @param row The current row of properties being validated
    * @returns A message if an error occurred while validating
    */
   private validateProperty(prop: string, val: string, row: any): string[] {
      let messages = [];
      switch (prop) {
         case "dateOfBirth":
            let timestamp = Date.parse(val);
            if (val && isNaN(timestamp)) {
               messages.push('The DOB field must be a valid date in the following format: YYYY-MM-DD');
            }
            break;
         case "notes":
            if (val && val.length > 0 && val.length > 1000) {
               messages.push('The maximum length for Notes is 1000 characters');
            }
            break;
         case "gender":
            if (val && ['m', 'f'].indexOf(val.toLowerCase()) === -1) {
               messages.push('Sex must be set to M or F');
            }
            break;
         case "company":
            if (val && val.length > 0 && val.length > 50) {
               messages.push('The maximum length for the Company field is 50 characters');
            }
            break;
         case "title":
            if (val && val.length > 0 && val.length > 50) {
               messages.push('The maximum length for the Title field is 50 characters');
            }
            break;
         case "firstName":
            if (val && val.length > 0 && val.length > 50) {
               messages.push('The maximum length for the First Name field is 50 characters');
            }
            break;
         case "lastName":
            if (val && val.length > 0 && val.length > 50) {
               messages.push('The maximum length for the Last Name field is 50 characters');
            }
            break;
         case "middleInitial":
            if (val && val.length > 0 && val.length > 1) {
               messages.push('The maximum length for the Middle Initial field is 1 character');
            }
            break;
         case "phone":
         case "phoneHome":
         case "phoneWork":
            // If country is provided in the import then use that, otherwise use the event's country
            let countryCode = row.country ? row.country : this.event.state.countryCode;
            if (val && val.length > 0 && !intlTelInputUtils.isValidNumber(val, countryCode)) {
               if (this.clearInvalidPhone) {
                  row[prop] = null;
               } else {
                  messages.push('An invalid phone number was provided');
               }
            }
            break;
         case "email":
            var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (val && val.length > 0) {
               if (!re.test(val)) {
                  messages.push('Email must be in a valid email format');
               } else if (_.contains(this.emails, val) && !this.allowDupEmail) {
                  messages.push('Duplicate guest email addresses cannot be imported.');
               } else {
                  this.emails.push(val);
               }
            }
            break;
         case "address1":
            if (val && val.length > 0 && val.length > 100) {
               messages.push('The maximum length for the Address1 field is 100 characters');
            }
            break;
         case "address2":
            if (val && val.length > 0 && val.length > 100) {
               messages.push('The maximum length for the Address2 field is 100 characters');
            }
            break;
         case "city":
            if (val && val.length > 0 && val.length > 50) {
               messages.push('The maximum length for the City field is 50 characters');
            }
            break;
         case "state":
            if (val && val.length > 0 && val.length > 3) {
               messages.push('State must be set to a valid 2 to 3 character state code');
            }
            break;
         case "country":
            if (val && val.length > 0 && val.length > 2) {
               messages.push('Country must be set to a valid 2 character country code');
            }
            break;
         case "postalCode":
            if (val && val.length > 0 && val.length > 10) {
               messages.push('Zip must be set to the valid zip or postal code for the target country');
            }
            break;
         case "locale":
            var re = /^[a-z]{2,3}(?:-[A-Z]{2,3}(?:-[a-zA-Z]{4})?)?$/gi;
            if (val && val.length > 0 && !re.test(val)) {
               messages.push('Locale must be set to the valid location/language code, ex: en-us');
            }
            break;
         case "salesOptin":
         case "contactOptin":
         case "promoOptin":
         case "contestOptin":
         case "unsubscribed":
            if (typeof val != 'undefined' && val.length > 0 && !ImporterService.utils.isBoolean(val)) {
               messages.push(`${ImporterService.fieldMap[prop]} must be set to true or false`);
            }
            break;
         case "isConfirmed":
            if (typeof val != 'undefined' && val.length > 0 && !ImporterService.utils.isBoolean(val)) {
               messages.push('Confirmed must be set to true or false');
            }
            break;
         case "guestType":
            if (typeof val != 'undefined' && val.length > 0 && this.guestTypes.indexOf(val) === -1) {
               messages.push('Guest type name does not match any campaign guest types');
            }
            break;
         case "source":
            if (val && val.length > 0 && val.length > 128) {
               messages.push('The maximum length for the Source field is 128 characters');
            }
            break;
         case "wristBandId":
            if (val && val.length > 0) {
               if (_.contains(this.wristBands, val)) {
                  messages.push('Duplicate wristband ID cannot be imported');
               } else {
                  this.wristBands.push(val);
               }
            }
            break;
         case "createdDate":
            if (val && isNaN(Date.parse(val))) {
               messages.push('The Date Created field must be a valid date in the format: MM/DD/YYYY');
            }
            break;
      }
      return messages;
   }
}
